import React, { useEffect, useState } from "react";
import Table from "../Shared/Table";
import api from "../../services/api";
import ConfigDetailsModal from "./ConfigDetailsModal";
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { finalizePDF } from '../Shared/ReportGenerator';
import Cookies from 'js-cookie';

function Settings({ userName: propUserName }) {
  const [tab, setTab] = useState("automatedAttendance");
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create"); // 'create' | 'edit'
  const [form, setForm] = useState({
    course_id: "",
    start_time: "",
    end_time: "",
  });
  const [editId, setEditId] = useState(null);
  const [professors, setProfessors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseLoading, setCourseLoading] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");

  // pagination and filtering for automated attendance list
  const [filterProfessor, setFilterProfessor] = useState("");
  const [filterWeekDays, setFilterWeekDays] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [summary, setSummary] = useState([]);

  const daysOfWeek = [
    "Segunda-feira",
    "Terça-feira",
    "Quarta-feira",
    "Quinta-feira",
    "Sexta-feira",
    "Sábado",
    "Domingo",
  ];
  const daysOfWeekMapping = React.useMemo(() => ({
    "Segunda-feira": 1,
    "Terça-feira": 2,
    "Quarta-feira": 3,
    "Quinta-feira": 4,
    "Sexta-feira": 5,
    "Sábado": 6,
    "Domingo": 7,
  }), []);

  useEffect(() => {
    if (tab === "automatedAttendance") {
      fetchConfigs();
      fetchProfessors();
      setCourses([]);
      setForm((prev) => ({ ...prev, course_id: "", professor_id: "" }));
    }
    // eslint-disable-next-line
  }, [tab]);

  const fetchConfigs = (opts = {}) => {
    setLoading(true);
    const params = {
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      ...opts,
    };
    if (filterProfessor) params.professor_id = filterProfessor;
    if (filterWeekDays && filterWeekDays.length) {
      params.week_days = filterWeekDays.map((d) => daysOfWeekMapping[d]).join(',');
    }

    api.get("/automated-attendance-configs", { params })
      .then((res) => {
        const data = res.data;
        const items = Array.isArray(data?.data) ? data.data : (data?.data || []);
        setConfigs(items);

        if (data?.meta) {
          setPagination((p) => ({
            ...p,
            currentPage: data.meta.current_page || p.currentPage,
            totalPages: data.meta.last_page || p.totalPages,
            totalItems: data.meta.total || p.totalItems,
            itemsPerPage: data.meta.per_page || p.itemsPerPage,
          }));
        }

        setError("");
      })
      .catch(() => setError("Erro ao carregar configurações."))
      .finally(() => setLoading(false));
  };

  const fetchProfessors = () => {
    api.get("/professors/index")
      .then((res) => setProfessors(res.data || []))
      .catch(() => setProfessors([]));
  };

  const fetchCoursesByProfessor = (professorId) => {
    setCourseLoading(true);
    // ask for a high limit to avoid pagination hiding some classes, since we
    // only need them client-side for selection
    api.get(`/courses/index`, { params: { professor_id: professorId, limit: 1000, page: 1 } })
      .then((res) => setCourses(res.data.data || []))
      .catch(() => setCourses([]))
      .finally(() => setCourseLoading(false));
  };

  const columns = [
    { key: "id", label: "ID" },
    { key: "course", label: "Turma", render: (v, row) => row.course?.name || row.attendance_session?.course?.name || "-" },
    { key: "professor", label: "Professor", render: (v, row) => row.professor?.name || (row.course?.professors?.[0]?.name) || "-" },
    { key: "weeks", label: "Dias da Semana", render: (v, row) => (row.weeks && row.weeks.length > 0) ? row.weeks.map(w => w.name.charAt(0).toUpperCase() + w.name.slice(1)).join(", ") : "-" },
    { key: "start_time", label: "Início" },
    { key: "end_time", label: "Fim" },
  ];

  const getActionItems = (id, item) => [
    {
      label: "Visualizar",
      action: () => {
        setLoading(true);
        api.get(`/automated-attendance-configs/${id}`)
          .then((res) => {
            setDetailsData(res.data?.data);
            setShowDetailsModal(true);
          })
          .catch(() => alert("Erro ao buscar detalhes."))
          .finally(() => setLoading(false));
      },
    },
    {
      label: "Editar",
      action: () => {
        setModalType("edit");
        setEditId(id);
        // ensure professor and category are set so courses list is populated
        const profId = item.professor?.id || (item.course?.professors?.[0]?.id) || "";
        const catId = item.course?.category?.id || "";

        setForm({
          professor_id: profId,
          course_id: item.course_id || "",
          start_time: item.start_time || "",
          end_time: item.end_time || "",
        });
        setSelectedCategory(catId);
        if (profId) {
          fetchCoursesByProfessor(profId);
        }
        setSelectedDays(item.week_days ? item.week_days.map((d) => daysOfWeek[d - 1]) : []);
        setShowModal(true);
      },
    },
    {
      label: "Excluir",
      action: () => {
        if (window.confirm("Tem certeza que deseja excluir esta configuração?")) {
          setLoading(true);
          api.delete(`/automated-attendance-configs/${id}`)
            .then(() => {
              fetchConfigs();
            })
            .catch(() => alert("Erro ao excluir."))
            .finally(() => setLoading(false));
        }
      },
    },
  ];

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === "professor_id") {
      // professor changed, clear related state
      setForm((prev) => ({ ...prev, professor_id: value, course_id: "" }));
      setSelectedCategory("");
      if (value) {
        fetchCoursesByProfessor(value);
      } else {
        setCourses([]);
      }
    } else if (name === "week_days") {
      setSelectedDays((prev) =>
        checked ? [...prev, value] : prev.filter((d) => d !== value)
      );
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // filter handlers
  const handleProfessorFilterChange = (e) => {
    setFilterProfessor(e.target.value);
    setPagination((p) => ({ ...p, currentPage: 1 }));
  };

  const handleWeekFilterChange = (e) => {
    const { value, checked } = e.target;
    setFilterWeekDays((prev) =>
      checked ? [...prev, value] : prev.filter((d) => d !== value)
    );
    setPagination((p) => ({ ...p, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, currentPage: page }));
  };

  const handleItemsPerPageChange = (event) => {
    const newItems = Number(event.target.value);
    setPagination((p) => ({
      ...p,
      itemsPerPage: newItems,
      currentPage: 1,
    }));
  };

  // derived data
  // When pagination or filters change, fetch from server
  useEffect(() => {
    if (tab === "automatedAttendance") {
      fetchConfigs();
    }
    // eslint-disable-next-line
  }, [pagination.currentPage, pagination.itemsPerPage, filterProfessor, filterWeekDays]);

  // print handler: fetch all configs for the user's polos and generate PDF report
  const handlePrint = async () => {
    try {
      // request grouped results by professor so the report is organized
      // do not send pagination params to the grouped endpoint
      const params = { group_by: 'professor' };
      const selectedPoloId = localStorage.getItem('selectedPoloId');
      if (selectedPoloId) params.polo_id = selectedPoloId;
      if (filterProfessor) params.professor_id = filterProfessor;
      if (filterWeekDays && filterWeekDays.length) params.week_days = filterWeekDays.map((d) => daysOfWeekMapping[d]).join(',');

      const res = await api.get('/automated-attendance-configs/group-by-professor', { params });
      const groups = Array.isArray(res.data?.data) ? res.data.data : (res.data?.data || []);
      const remoteSummary = Array.isArray(res.data?.summary) ? res.data.summary : [];
      setSummary(remoteSummary);

      const doc = new jsPDF('landscape');
      const head = [['ID', 'Turma', 'Dias', 'Início', 'Fim', 'Alunos']];
      const userName = propUserName || Cookies.get('userName') || '';
      const filters = `Professor: ${filterProfessor || 'todos'}; Datas: ${filterWeekDays && filterWeekDays.length ? filterWeekDays.join(', ') : 'todas'}`;

      const headerHeight = 36;
      let cursorY = headerHeight;

      // iterate groups and render a table per professor
      for (let gi = 0; gi < groups.length; gi++) {
        const group = groups[gi];
        const professorName = (group && group.professor && group.professor.name) ? group.professor.name : 'Sem professor';
        // new API returns group.attendance.data and group.attendance.total
        const items = Array.isArray(group?.attendance?.data) ? group.attendance.data : (Array.isArray(group?.items) ? group.items : []);

        // sort group's items by week id then start_time
        const sorted = [...items].sort((a, b) => {
          const aIds = (a.weeks || []).map((w) => w.id || Infinity);
          const bIds = (b.weeks || []).map((w) => w.id || Infinity);
          const aMin = Math.min(...(aIds.length ? aIds : [Infinity]));
          const bMin = Math.min(...(bIds.length ? bIds : [Infinity]));
          if (aMin !== bMin) return aMin - bMin;
          if ((a.start_time || '') < (b.start_time || '')) return -1;
          if ((a.start_time || '') > (b.start_time || '')) return 1;
          return 0;
        });

        const body = sorted.map((c) => [
          c.id,
          c.course?.name || '-',
          c.weeks && c.weeks.length ? c.weeks.map(w => (w.name.charAt(0).toUpperCase() + w.name.slice(1))).join(', ') : '-',
          c.start_time || '',
          c.end_time || '',
          // prefer students_count (from withCount) else fallback to loaded students length
          (c.course && (c.course.students_count ?? (c.course.students ? c.course.students.length : 0))) || 0,
        ]);

        // add professor heading
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        // if not first group and we're not at top, create some spacing
        if (gi > 0) {
          cursorY = doc.lastAutoTable ? (doc.lastAutoTable.finalY + 10) : cursorY + 10;
        }
        // ensure start on a new page if cursorY is near bottom
        const pageH = doc.internal.pageSize.getHeight();
        if (cursorY + 20 > pageH - 20) {
          doc.addPage();
          cursorY = headerHeight;
        }
        doc.text(professorName, 14, cursorY + 6);
        cursorY += 8;

        // draw table for this professor
        // @ts-ignore
        doc.autoTable({
          head,
          body,
          startY: cursorY,
          margin: { top: headerHeight },
        });

        cursorY = doc.lastAutoTable ? (doc.lastAutoTable.finalY + 8) : headerHeight;
      }

      // finalize headers/footers for the full document using actual page count
      const totalRecords = groups.reduce((acc, g) => {
        const cnt = Array.isArray(g?.attendance?.data) ? g.attendance.data.length : (Array.isArray(g?.items) ? g.items.length : 0);
        return acc + cnt;
      }, 0);

      // add summary table from backend (unique student count by weekday/shift)
      if (remoteSummary.length > 0) {
        const summaryHead = [['Dia da semana', 'Turno', 'Alunos']];
        const summaryBody = remoteSummary.map((s) => [s.weekday, s.shift, s.students]);

        const startY = doc.lastAutoTable ? (doc.lastAutoTable.finalY + 12) : 60;
        doc.setFontSize(12);
        doc.text('Resumo de quantidade de alunos por dia/turno', 14, startY);
        // @ts-ignore
        doc.autoTable({
          head: summaryHead,
          body: summaryBody,
          startY: startY + 6,
          styles: { fontSize: 9 },
          headStyles: { fillColor: [77, 182, 172] },
        });
      }

      // draw headers and footer watermark across all pages
      finalizePDF(doc, userName, filters, totalRecords, 'Chamada Automática');

      doc.save('chamada-automatica.pdf');
    } catch (err) {
      console.error(err);
      alert('Erro ao gerar impressão.');
    }
  };


  useEffect(() => {
    setForm((prev) => ({ ...prev, week_days: selectedDays.map((d) => daysOfWeekMapping[d]) }));
  }, [selectedDays, daysOfWeekMapping]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form };
    if (!payload.week_days || !Array.isArray(payload.week_days)) {
      payload.week_days = [];
    }
    if (modalType === "create") {
      api.post("/automated-attendance-configs", payload)
        .then(() => {
          setShowModal(false);
          fetchConfigs();
        })
        .catch(() => alert("Erro ao criar."))
        .finally(() => setLoading(false));
    } else if (modalType === "edit" && editId) {
      api.put(`/automated-attendance-configs/${editId}`, payload)
        .then(() => {
          setShowModal(false);
          fetchConfigs();
        })
        .catch(() => alert("Erro ao atualizar."))
        .finally(() => setLoading(false));
    }
  };

  const openCreateModal = () => {
    setModalType("create");
    setForm({ course_id: "", start_time: "", end_time: "", professor_id: "", week_days: [] });
    setSelectedDays([]);
    setSelectedCategory("");
    setCourses([]);
    setShowModal(true);
    setEditId(null);
  };

  // Extrai as categorias únicas das turmas carregadas
  const categories = React.useMemo(() => {
    const cats = [];
    courses.forEach((c) => {
      if (c.category && !cats.find((cat) => cat.id === c.category.id)) {
        cats.push(c.category);
      }
    });
    return cats;
  }, [courses]);

  // Filtra as turmas pela categoria selecionada
  const filteredCourses = React.useMemo(() => {
    if (!selectedCategory) return courses;
    return courses.filter((c) => c.category && c.category.id === Number(selectedCategory));
  }, [courses, selectedCategory]);

  return (
    <div className="list-containet-tab">
      <div className="tabs" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <button
            className={`tab-button ${tab === "automatedAttendance" ? "active" : ""}`}
            onClick={() => setTab("automatedAttendance")}
          >
            Chamada Automática
          </button>
          {/* Outras abas futuras
          <button
            className={`tab-button ${tab === "outraAba" ? "active" : ""}`}
            onClick={() => setTab("outraAba")}
          >
            Outra Aba
          </button> */}
        </div>
        <div className="modal-buttons">
          <button className="btn-primary" style={{ minWidth: 180 }} onClick={openCreateModal}>
            Nova Configuração
          </button>
          <button
            className="btn-secondary"
            style={{ minWidth: 180, marginLeft: 8 }}
            onClick={handlePrint}
          >
            Imprimir
          </button>
        </div>
      </div>
      <div className="tab-content">
        {tab === "automatedAttendance" && (
          <>
            {/* filters panel */}
            <div className="filters" style={{ marginBottom: 16 }}>
              <div className="filter-group">
                <label>Professor: </label>
                <select
                  value={filterProfessor}
                  onChange={handleProfessorFilterChange}
                >
                  <option value="">Todos</option>
                  {professors.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <fieldset style={{ display: "inline-block", marginLeft: 24 }}>
                <legend>Dias da semana:</legend>
                {daysOfWeek.map((day) => (
                  <label key={day} style={{ marginRight: 8 }}>
                    <input
                      type="checkbox"
                      value={day}
                      checked={filterWeekDays.includes(day)}
                      onChange={handleWeekFilterChange}
                    />
                    {day}
                  </label>
                ))}
              </fieldset>
            </div>
            <Table
              data={configs}
              columns={columns}
              loading={loading}
              error={error}
              getActionItems={getActionItems}
              handleSort={() => {}}
              sortColumn={null}
              sortOrder={null}
              itemsPerPage={pagination.itemsPerPage}
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
            {/* pagination controls */}
            <div className="pagination" style={{ marginTop: 8 }}>
              <button
                onClick={() => handlePageChange(1)}
                disabled={pagination.currentPage === 1}
              >
                {"<<"}
              </button>
              <button
                onClick={() =>
                  handlePageChange(pagination.currentPage - 1)
                }
                disabled={pagination.currentPage === 1}
              >
                {"<"}
              </button>
              <span>
                Página {pagination.currentPage} de {pagination.totalPages}
              </span>
              <button
                onClick={() =>
                  handlePageChange(pagination.currentPage + 1)
                }
                disabled={pagination.currentPage === pagination.totalPages}
              >
                {">"}
              </button>
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                {">>"}
              </button>
            </div>
            <div className="items-per-page-selector" style={{ marginTop: 8 }}>
              <label htmlFor="itemsPerPage">Itens por página: </label>
              <select
                id="itemsPerPage"
                value={pagination.itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
              </select>
            </div>

            {summary && summary.length > 0 && (
              <div style={{ marginTop: 24, background: '#ffffff', border: '1px solid #d1d5db', borderRadius: 8, padding: 16 }}>
                <h3 style={{ marginTop: 0 }}>Resumo de Alunos por Dia/Turno</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Dia da semana</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Turno</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Alunos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((s, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>{s.weekday}</td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>{s.shift}</td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>{s.students}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{modalType === "create" ? "Nova Configuração" : "Editar Configuração"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Professor</label>
                <select name="professor_id" value={form.professor_id || ""} onChange={handleInputChange} required>
                  <option value="">Selecione</option>
                  {professors.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              {/* NOVO INPUT DE OFICINA */}
              <div className="form-group">
                <label>Oficina</label>
                <select
                  name="category_id"
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  disabled={courses.length === 0}
                  required
                >
                  <option value="">Selecione</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Turma</label>
                <select name="course_id" value={form.course_id} onChange={handleInputChange} required disabled={courseLoading || !form.professor_id || !selectedCategory}>
                  <option value="">Selecione</option>
                  {filteredCourses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <fieldset>
                <legend>Dias da Semana:</legend>
                <div className="category-checkbox-item">
                  {daysOfWeek.map((day) => (
                    <label key={day} >
                      <input
                        type="checkbox"
                        name="week_days"
                        value={day}
                        checked={selectedDays.includes(day)}
                        onChange={handleInputChange}
                      />
                      {day}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="form-group">
                <label>Início</label>
                <input type="time" name="start_time" value={form.start_time} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Fim</label>
                <input type="time" name="end_time" value={form.end_time} onChange={handleInputChange} required />
              </div>
              <div className="modal-buttons" style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {modalType === "create" ? "Criar" : "Salvar"}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)} disabled={loading}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showDetailsModal && (
        <ConfigDetailsModal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          data={detailsData}
        />
      )}
    </div>
  );
}

export default Settings;
